import React from 'react'
import NextImage, { StaticImageData } from 'next/image'
import { useRouter } from 'next/router'
import useCurrentUser from '@/hooks/useCurrentUser'
import { Routes } from '@/assets/configs'
import { usePetsittingMode } from '@/store/usePetsittingMode'
import { useIsQueryOwner, useIsResponsive } from '@/hooks'
import {
  EmtFooter,
  EmtFooterProps,
  EmtTypography,
  FacebookLogo,
  InstagramLogo,
  PinterestLogo,
  TwitterLogo,
} from 'emt-components-v2'
import Box from '@mui/material/Box'
import { i18n, useTranslation } from 'next-i18next'
import NextALink from '@/components/NextALink'
import FooterNextLink from './FooterNextLink'

import VariantImages from '@/assets/VariantImages'
import { SxProps } from '@mui/material/styles'
import { DateTime } from 'luxon'

import useRNWebViewTranslation from '@/hooks/useRNWebViewTranslation'
import { EmergencyPetCareRelayBanner } from './EmergencyPetCareRelayBanner'
import Container from '@mui/material/Container'

type Props = {
  isLogged?: boolean
  topDesktopSrc?: boolean | string | StaticImageData
  topMobileSrc?: boolean | string | StaticImageData
  topImageNode?: React.ReactNode
  topImageNodeSx?: SxProps
}
const isPetsittingMode = usePetsittingMode.getState().isPetsittingMode

// Sign in / sign up pages: the Emergency Pet Care Relay banner is hidden there
const authRoutePrefixes = [
  Routes.Connection.SignIn,
  Routes.Connection.Welcome,
  Routes.Connection.PasswordForgotten,
  Routes.Connection.PasswordEmailSent,
  Routes.Connection.ConnectionLink,
  Routes.Connection.SignUp.Type,
  '/create-your-profile',
  Routes.Connection.CreateAccount.SignUpCallRequest,
  '/pro/sign-up',
  '/reset-password',
  Routes.EmergencyPetCareRelay,
]
const noEmergencyPetCareRelayBannerRoute = (pathname: string) =>
  authRoutePrefixes.some((prefix) => pathname.startsWith(prefix))

const Footer: React.FC<Props> = ({
  topDesktopSrc = isPetsittingMode ? VariantImages.FooterTopDesktopPurple : VariantImages.FooterTopDesktop,
  topMobileSrc = isPetsittingMode ? VariantImages.FooterTopMobilePurple : VariantImages.FooterTopMobile,
  topImageNode,
  topImageNodeSx,
  isLogged = true,
}) => {
  const [isMobile] = useIsResponsive()
  const { currentUser } = useCurrentUser()
  const { t } = useTranslation('common')
  const hasRNWebView = useRNWebViewTranslation()
  const { pathname } = useRouter()

  const [, type, appendQueryType] = useIsQueryOwner(true)

  const footerTopSrc = isMobile ? topMobileSrc : topDesktopSrc

  const footerParams: EmtFooterProps = {
    footerTop: (
      <>
        {!noEmergencyPetCareRelayBannerRoute(pathname) && (
          <Container>
            <EmergencyPetCareRelayBanner sx={{ marginTop: '3em' }} />
          </Container>
        )}
        <Box sx={{ textAlign: 'center', marginTop: '3rem', marginBottom: '1rem', ...topImageNodeSx }}>
          {topImageNode ||
            (!!footerTopSrc && typeof footerTopSrc !== 'boolean' && (
              <NextImage
                src={footerTopSrc}
                quality={100}
                alt={'footer image'}
                style={{ objectFit: 'contain', maxWidth: '100%', margin: 'auto' }}
              />
            ))}
        </Box>
      </>
    ),
    footerBottom: {
      text: isMobile
        ? undefined
        : isPetsittingMode
        ? 'Partagez vos photos sur Facebook et Instagram #supersittersapp'
        : t('footer.footerBottom.text') !== '#'
        ? t('footer.footerBottom.text')
        : undefined,
      copyright: isPetsittingMode
        ? `Copyright 2016 - ${DateTime.now().year} Super Sitters`
        : t('footer.footerBottom.copyright', { date: DateTime.now().year }),
    },
    logoLinks: (
      <Box sx={{ display: 'flex', flexDirection: 'column', maxWidth: '220px' }}>
        <NextALink href={appendQueryType(type === 'pro' ? Routes.Pro.Dashboard : Routes.Home)}>
          <NextImage
            src={isPetsittingMode ? VariantImages.LogoSitterWhiteFull : VariantImages.Logo}
            alt={t('footer.logo.alt')}
            title={t('footer.logo.title') as string}
            className="logo"
            quality={100}
            style={{ maxHeight: '98px' }}
          />
        </NextALink>
        {!hasRNWebView && (VariantImages.GooglePlay || VariantImages.AppStore) ? (
          <Box
            sx={{
              display: 'flex',
              marginTop: '15px',
              gap: '8px',
            }}
          >
            {VariantImages.AppStore && (
              <NextALink
                href={
                  isPetsittingMode
                    ? 'https://apps.apple.com/fr/app/super-sitters/id6759970836'
                    : `https://apps.apple.com/${i18n?.language}/app/id1403471929`
                }
                title="appstore"
              >
                <NextImage src={VariantImages.AppStore} alt={'App store'} />
              </NextALink>
            )}
            {VariantImages.GooglePlay && (
              <NextALink
                href={
                  isPetsittingMode
                    ? 'https://play.google.com/store/apps/details?id=app.supersitters.app'
                    : `https://play.google.com/store/apps/details?id=com.empruntemontoutou.app&hl=${i18n?.language}`
                }
                title="playstore"
              >
                <NextImage alt={'Google Play'} src={VariantImages.GooglePlay} />
              </NextALink>
            )}
          </Box>
        ) : null}
      </Box>
    ),
    firstLinks: t('footer.firstLinks.title') !== '#' && (
      <>
        <EmtTypography variant="h5" color="white">
          {t('footer.firstLinks.title')}
        </EmtTypography>
        <FooterNextLink
          href={isPetsittingMode ? t('footer.linksPetsitting.insurance.url') : t('footer.links.insurance.url')}
          title={t('footer.links.insurance.title')}
        />
        {!hasRNWebView && !isPetsittingMode && (
          <FooterNextLink href={t('footer.links.pact.url')} title={t('footer.links.pact.title')} />
        )}
        <FooterNextLink
          href={
            !currentUser && isPetsittingMode
              ? 'https://www.supersitters.app/confiance-et-securite/'
              : !currentUser && !isPetsittingMode
              ? 'https://www.empruntemontoutou.com/confiance-et-securite/'
              : type === 'pro'
              ? Routes.Pro.Safety
              : Routes.Safety
          }
          title={t('footer.links.security.title')}
        />
        <FooterNextLink
          href={isPetsittingMode ? t('footer.linksPetsitting.tos.url') : t('footer.links.tos.url')}
          title={t('footer.links.tos.title')}
        />
        <FooterNextLink
          href={isPetsittingMode ? t('footer.linksPetsitting.legals.url') : t('footer.links.legals.url')}
          title={t('footer.links.legals.title')}
        />
      </>
    ),
    secondLinks: t('footer.secondLinks.title') !== '#' && !hasRNWebView && (
      <>
        <EmtTypography variant="h5" color="white">
          {t('footer.secondLinks.title')}
        </EmtTypography>
        <FooterNextLink
          href={isPetsittingMode ? t('footer.linksPetsitting.reviews.url') : t('footer.links.reviews.url')}
          title={t('footer.links.reviews.title')}
        />
        <FooterNextLink
          href={isPetsittingMode ? t('footer.linksPetsitting.download.url') : t('footer.links.download.url')}
          title={t('footer.links.download.title')}
        />
        {!isPetsittingMode && <FooterNextLink href={t('footer.links.blog.url')} title={t('footer.links.blog.title')} />}
      </>
    ),
    thirdLinks: t('footer.thirdLinks.title') !== '#' && (
      <>
        <EmtTypography variant="h5" color="white">
          {t('footer.thirdLinks.title')}
        </EmtTypography>
        <FooterNextLink
          href={isPetsittingMode ? t('footer.linksPetsitting.help.url') : t('footer.links.help.url')}
          title={t('footer.links.help.title')}
        />
        <FooterNextLink
          href={
            isPetsittingMode
              ? t('footer.linksPetsitting.contact.url')
              : isLogged
              ? appendQueryType(type === 'pro' ? Routes.Pro.Contact : Routes.Contact)
              : t('footer.links.contact.url')
          }
          title={t('footer.links.contact.title')}
        />
        {!hasRNWebView && (
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '15px', paddingTop: '15px' }}>
            {isPetsittingMode ? (
              <>
                {t('footer.social.petsitting.facebook') !== '#' && (
                  <NextALink href={t('footer.social.petsitting.facebook')!} title="facebook">
                    <FacebookLogo color="white" size="medium" />
                  </NextALink>
                )}
                {t('footer.social.petsitting.instagram') !== '#' && (
                  <NextALink href={t('footer.social.petsitting.instagram')!} title="instagram">
                    <InstagramLogo color="white" />
                  </NextALink>
                )}
              </>
            ) : (
              <>
                {t('footer.social.facebook') !== '#' && (
                  <NextALink href={t('footer.social.facebook')!} title="facebook">
                    <FacebookLogo color="white" size="medium" />
                  </NextALink>
                )}
                {t('footer.social.instagram') !== '#' && (
                  <NextALink href={t('footer.social.instagram')!} title="instagram">
                    <InstagramLogo color="white" />
                  </NextALink>
                )}
                {t('footer.social.pinterest') !== '#' && (
                  <NextALink href={t('footer.social.pinterest')!} title="pinterest">
                    <PinterestLogo color="white" />
                  </NextALink>
                )}
                {t('footer.social.twitter') !== '#' && (
                  <NextALink href={t('footer.social.twitter')!} title="twitter">
                    <TwitterLogo color="white" />
                  </NextALink>
                )}
              </>
            )}
          </Box>
        )}

        {isMobile && !hasRNWebView ? (
          <EmtTypography variant="caption" color="common.white" sx={{ display: 'block', marginTop: '10px' }}>
            {t('footer.footerBottom.text')}
          </EmtTypography>
        ) : null}
      </>
    ),
  }

  return <EmtFooter {...footerParams} />
}

export default Footer
